/**
 * MC Cart Shipping Bar — Live free-shipping progress at the top of the cart drawer.
 *
 * Server-side renders initial state via Liquid. JS keeps it in sync on cart events.
 * Supports two layouts:
 *   - default: message + bar
 *   - hero:    [check + status]  [threshold]
 *              [-------- progress bar --------]
 *              [multiplier or remaining]  [total]
 *
 * Refreshes on cart:updated / cart:items-added events (Horizon-compatible).
 */
(function () {
  if (window.customElements.get('mc-shipping-bar')) return;

  class McShippingBar extends HTMLElement {
    connectedCallback() {
      this.threshold = Math.max(1, parseInt(this.dataset.threshold || '25000', 10));
      this.thresholdLabel = this.dataset.thresholdLabel || '';
      this.progressTemplate = this.dataset.progressMessage || "You're [amount] away from free shipping";
      this.qualifiedMessage = this.dataset.qualifiedMessage || "Free shipping unlocked";
      this.isHero = this.classList.contains('mc-shipping-bar--hero');

      this.messageEl = this.querySelector('[data-mcsb-message]');
      this.fillEl = this.querySelector('[data-mcsb-fill]');
      this.multiplierEl = this.querySelector('[data-mcsb-multiplier]');
      this.totalEl = this.querySelector('[data-mcsb-total]');
      this.thresholdEl = this.querySelector('[data-mcsb-threshold-label]');

      this.boundUpdate = (e) => this.#update(e?.detail?.cart || null);
      document.addEventListener('cart:updated', this.boundUpdate);
      document.addEventListener('cart:items-added', this.boundUpdate);

      // Server-side already rendered the initial state; no fetch needed on mount.
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this.boundUpdate);
      document.removeEventListener('cart:items-added', this.boundUpdate);
    }

    async #update(detailCart) {
      try {
        let cart = detailCart;
        if (!cart || !cart.items) {
          cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(r => r.json());
        }
        if (!cart || !cart.items || cart.items.length === 0) {
          this.setAttribute('hidden', '');
          return;
        }
        this.removeAttribute('hidden');

        const total = cart.total_price || 0;
        const remaining = Math.max(0, this.threshold - total);
        const progress = Math.min(100, Math.max(0, (total / this.threshold) * 100));
        const qualified = remaining <= 0;

        if (this.fillEl) this.fillEl.style.width = progress + '%';
        this.classList.toggle('mc-shipping-bar--qualified', qualified);

        if (this.messageEl) {
          if (qualified) {
            this.messageEl.textContent = this.qualifiedMessage;
          } else {
            const amount = this.#formatMoney(remaining);
            this.messageEl.textContent = this.progressTemplate.replace('[amount]', amount);
          }
        }

        if (this.isHero) {
          if (this.totalEl) this.totalEl.textContent = this.#formatMoney(total);
          if (this.multiplierEl) {
            if (qualified) {
              const multiplier = (total / this.threshold).toFixed(1);
              this.multiplierEl.textContent = multiplier + '× past threshold';
            } else {
              this.multiplierEl.textContent = 'add ' + this.#formatMoney(remaining) + ' to unlock';
            }
          }
        }
      } catch {
        // Network error — keep last known state rather than thrashing the UI
      }
    }

    #formatMoney(cents) {
      const n = (parseInt(cents, 10) || 0) / 100;
      return '$' + n.toFixed(2);
    }
  }

  customElements.define('mc-shipping-bar', McShippingBar);
})();
